import * as React from "react";
import { Shield, Download, DollarSign, Package, MapPin, Tag } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";

interface Summary {
  totalValue: number;
  itemCount: number;
  valuedItemCount: number;
  bySpace: { spaceId: string | null; spaceName: string | null; totalValue: number; itemCount: number }[];
  byTag: { tag: string; totalValue: number; itemCount: number }[];
}

const fmt = (cents: number) => "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 });

export default function ValuationPage() {
  const toast = useToast();
  const [data, setData] = React.useState<Summary | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    apiFetch<Summary>("/api/valuation/summary").then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleExport = () => {
    window.open(`${import.meta.env.VITE_API_URL ?? ""}/api/valuation/export`, "_blank");
    toast.show("Report opened — use Print → Save as PDF");
  };

  if (loading) return <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-clay-muted rounded-lg animate-pulse" />)}</div>;
  if (!data)
    return (
      <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
        <p className="text-error-text mb-4">We couldn't load your valuation data.</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">Try again</button>
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Shield className="w-6 h-6 text-primary" />Insurance &amp; Valuation</h1>
        <button onClick={handleExport} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-1 hover:bg-amethyst-ink transition-colors"><Download className="w-4 h-4" />Export Report</button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-clay-border p-4">
          <DollarSign className="w-5 h-5 text-primary mb-1" />
          <p className="text-2xl font-semibold text-ink tabular-nums">{fmt(data.totalValue)}</p>
          <p className="text-[0.8125rem] text-faint">Total Estimated Value</p>
        </div>
        <div className="bg-white rounded-lg border border-clay-border p-4">
          <Package className="w-5 h-5 text-primary mb-1" />
          <p className="text-2xl font-semibold text-ink tabular-nums">{data.valuedItemCount}</p>
          <p className="text-[0.8125rem] text-faint">Items Valued</p>
        </div>
        <div className="bg-white rounded-lg border border-clay-border p-4">
          <Package className="w-5 h-5 text-faint mb-1" />
          <p className="text-2xl font-semibold text-ink tabular-nums">{data.itemCount - data.valuedItemCount}</p>
          <p className="text-[0.8125rem] text-faint">Items Unvalued</p>
        </div>
      </div>

      {/* By Space */}
      {data.bySpace.length > 0 && (
        <section className="bg-white rounded-lg border border-clay-border p-6">
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Value by Location</h2>
          <div className="space-y-2">
            {data.bySpace.sort((a, b) => b.totalValue - a.totalValue).map((s) => (
              <div key={s.spaceId ?? "none"} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-body">{s.spaceName ?? "Unassigned"}</span>
                <span className="text-[0.8125rem] text-faint tabular-nums">{s.itemCount} items</span>
                <span className="text-sm font-medium text-ink tabular-nums">{fmt(s.totalValue)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* By Tag */}
      {data.byTag.length > 0 && (
        <section className="bg-white rounded-lg border border-clay-border p-6">
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-primary" />Value by Tag</h2>
          <div className="space-y-2">
            {data.byTag.sort((a, b) => b.totalValue - a.totalValue).map((t) => (
              <div key={t.tag} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-body">{t.tag}</span>
                <span className="text-[0.8125rem] text-faint tabular-nums">{t.itemCount} items</span>
                <span className="text-sm font-medium text-ink tabular-nums">{fmt(t.totalValue)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.valuedItemCount === 0 && (
        <div className="text-center py-12 bg-sage-wash rounded-lg border border-clay-border">
          <DollarSign className="w-12 h-12 text-sage-deep mx-auto mb-3" />
          <p className="text-[1.0625rem] font-semibold text-ink mb-1">No items have been valued yet</p>
          <p className="text-sm text-body">Add estimated values to your items to see your inventory valuation</p>
        </div>
      )}
    </div>
  );
}
